import {Injectable, ViewChild} from '@angular/core';
import {HttpClient} from "@angular/common/http";
import {GetTicketModel, TicketAttachmentModel, TicketModel, TicketNoteModel} from "@app/model/ticket.model";
import {BehaviorSubject, Observable} from "rxjs";
import {AlertsService} from "@app/services/misc-services/alerts.service";
import {Base64ConversionService} from "@app/services/misc-services/base64-conversion.service";
import {GlobalSpinnerService} from "@app/services/misc-services/global-spinner.service";
import {Router} from "@angular/router";
import {PortalUser} from "@app/model/portal-user.model";
import {MatTableDataSource} from "@angular/material/table";
import {MatPaginator} from "@angular/material/paginator";
import {MatSort} from "@angular/material/sort";
import moment from "moment-timezone";

@Injectable({
    providedIn: 'root'
})
export class TicketsService {
    selectedTicket: BehaviorSubject<GetTicketModel> = new BehaviorSubject<GetTicketModel>(null);
    selectedTicketAttachments: BehaviorSubject<TicketAttachmentModel[]> = new BehaviorSubject<TicketAttachmentModel[]>([]);
    attachmentsToUpload: {fileName: string, fileAsBase64: string}[] = [];
    tickets: MatTableDataSource<GetTicketModel[]> = new MatTableDataSource<GetTicketModel[]>([]);
    pageSizeOptions: number[] = [];
    @ViewChild(MatPaginator) paginator: MatPaginator;
    @ViewChild(MatSort) sort: MatSort;

    constructor(private http: HttpClient,
                public alert: AlertsService,
                public B64Service: Base64ConversionService,
                public globalSpinner: GlobalSpinnerService,
                public router: Router) {
    }

    clearActiveTicket() {
        this.router.navigate(['/tickets'])
    }
    createNoteDTO(notes: string, portalUser: PortalUser, ticketNumber: number): TicketNoteModel {
        const timeStamp = moment(new Date).tz('America/Denver').toISOString(true);
        return  {
            ticketNumber: ticketNumber,
            noteNumber: 0,
            version: 0,
            createdBy: portalUser.username,
            note: notes,
            privateNote: false,
            lastUpdated: timeStamp.substring(0, timeStamp.length - 6)
        }
    }

    createTicketDTO(portalUser: PortalUser, title: string, type?: string): TicketModel {
        return {
            title: title,
            ticketNumber: 0,
            status: 'O',
            source: 'WEB',
            requestedFor: portalUser.cuid,
            createdBy: 'WEB',
            contact: portalUser.firstName + ' ' + portalUser.lastName,
            contactEmailAddress: portalUser.emailAddress,
            ticketType: type || ' ',
            assignedTo: '_WEB',
        }
    }

    createFullTicketObject(ticket: TicketModel, note: TicketNoteModel, username: string) {
        return {notes: [note], ticket: JSON.stringify(ticket), userWhoSaved: username}
    }

    createEnhancementNoteDTO(ticketId: number, notes: string, activeUser: PortalUser, ticketPoints: number, description: string) {
        return {
            id: ticketId,
            html: notes,
            privateNote: false,
            supportRep: '',
            user: activeUser.username,
            statusEmail: activeUser.emailAddress,
            ticketPoints: ticketPoints,
            desc: description
        }
    }

    ticketsByCUID(cuid: string): Observable<any> {
        return this.http.get(`/api/secured/ticket/ticketsByCUID/${cuid}`)
    }

    getTicketAndAttachments(ticketNumber: number) {
        return Promise.all([this.getTicket(ticketNumber), this.getAttachments(ticketNumber)]);
    }

    getTicket(ticketNumber: number) {
        return this.http.get(`/api/secured/ticket/get/${ticketNumber}`)
    }

    getAttachments(ticketNumber: number) {
        return this.http.get(`/api/secured/ticket/attachment/info/list/${ticketNumber}`)
    }

    saveTicketNote(note: TicketNoteModel) {
        return this.http.post(`/api/secured/ticket/createTicketNote`, note);
    }

    createTicket(ticket: TicketModel, note: TicketNoteModel, portalUser: PortalUser) {
        return this.http.post(`/api/secured/ticket/createTicket`,
            {saveTicket: this.createFullTicketObject(ticket, note, portalUser.username)})
    }

    updateTicket(ticket: TicketModel, portalUser: PortalUser) {
        return this.http.post(`/api/secured/ticket/createTicket`, {
            saveTicket: {
                ticket: JSON.stringify(ticket),
                notes: this.selectedTicket.value.notes,
                userWhoSaved: portalUser.username
            }
        })
    }

    // notes: [note], ticket: JSON.stringify(ticket), userWhoSaved: username

    refreshTicketsAndGoBack() {
        this.tickets.data = [];
        this.attachmentsToUpload = [];
        this.router.navigate(['/tickets']);
    }

    reopenClosedTicket(ticket: TicketModel, note: TicketNoteModel, portalUser: PortalUser){
        // TODO: Not sure if this is how this should work... this is just creating a new ticket...
        ticket.percentComplete = 95;
        ticket.status = 'O';
        return this.http.post(`/api/secured/ticket/createTicket`,
            {saveTicket: this.createFullTicketObject(ticket, note, portalUser.username)}).subscribe(() => {
                this.alert.getAlert('Ticket reopened.', 'X')
            },
            () => {
                this.alert.getError('Unable to reopen ticket, please try again.', 'X')
            })
    }

    createDeployment(ticket: TicketModel, note: TicketNoteModel, portalUser: PortalUser) {
        ticket.createdBy = 'WEB';
        ticket.source = 'WEB';
        ticket.assignedTo = '_DEPLOYMENT';
        ticket.supportRepresentative = '';
        return this.http.post(`/api/secured/ticket/createTicket`, {saveTicket: this.createFullTicketObject(ticket, note, portalUser.username)})
    }

    createHraTicket(note: TicketNoteModel, portalUser: PortalUser){
        const ticket = this.createTicketDTO(portalUser, ('FLEX Teller HRA setup for ' + portalUser.cuid))
        ticket.assignedTo = '_PCWEB';
        ticket.supportRepresentative = ''
        ticket.createdBy = 'WEB';
        ticket.source = 'WEB';
        return this.createTicket(ticket, note, portalUser);
    }

    // Currently not being used, keeping in so that when enhancement ticket stuff is working we can use this again.
    // createEnhancementTicket(portalUser: PortalUser, ticket: TicketModel, note: TicketNoteModel, points?: number, totalCuPoints?: number) {
    //     return this.http.post(`/api/secured/ticket/createNewTicket`,
    //         {
    //             id: 0,
    //             cuid: portalUser.cuid,
    //             title: ticket.title,
    //             user: portalUser.username,
    //             description: note.note || '',
    //             notes: [note.note],
    //             userEmail: portalUser.emailAddress,
    //             contact: {firstName: portalUser.firstName, lastName: portalUser.lastName},
    //             assignedTo: '_WEB',
    //             ticketType: ticket.ticketType
    //         }
    //     )
    // }

    updateCommunityPoints(cuid: string, points: number) {
        this.http.post('/api/secured/creditUnion/updateFlexPointsByCuid', {cuid: cuid, points: points})
    }

    closeTicketRequest(ticketNumber: number, reason: string, user: string, supportRep: string, status: string) {
        return this.http.post(`/api/secured/ticket/closeTicketRequest`, {
            id: ticketNumber,
            reason: reason,
            user: user,
            supportRep: supportRep,
            status: status,
        })
    }

    getAssigneeList() {
        return this.http.get('/api/secured/ticket/assignee/list')
    }

    getTicketType(type){
        switch (type) {
            case ' ' || '' : {
                return 'General support';
            }
            case 'E': {
                return 'Credit Union Paid Enhancement';
            }
            case 'C': {
                return 'Community Points';
            }
            case 'A': {
                return 'Any';
            }
            case 'F': {
                return 'Fix';
            }
            case 'D': {
                return 'Duplicate';
            }
            case 'G': {
                return 'General Web';
            }
            case 'I': {
                return 'Internal';
            }
            case 'T': {
                return 'Training Issue';
            }
            case 'O': {
                return 'Other';
            }
            default : {
                return 'Support'
            }
        }
    }

    downloadAttachment(fileName: string) {
        this.http.get(`/api/secured/ticket/attachment/downloadasbase64/${this.selectedTicket.value.ticket.ticketNumber}/${fileName}`)
            .subscribe((attachment: {base64: string}) => this.B64Service.downloadB64File(attachment.base64, fileName))
    }

    uploadAttachmentList(ticketNumber) {
        this.attachmentsToUpload.forEach((attachment) => {
            this.http.post(`/api/secured/ticket/attachment/uploadAttachment/${ticketNumber}/${attachment.fileName}`,
                {file: attachment.fileAsBase64}).subscribe(() => {
                    this.attachmentsToUpload = [];
                    this.getAttachments(ticketNumber).subscribe((attachments: TicketAttachmentModel[]) => this.selectedTicketAttachments.next(attachments))
            }, () => {
                    this.alert.getError('Unable to upload attachments', 'X');
            })
        })
    }

    fileSelected(event) {
        console.error(event)
        const fileList = [];
        Object.entries(event).forEach((file) => {
            fileList.push(file[1])
        })
        if(fileList.length > 0) {
            fileList.forEach((file) => {
                const mime = this.B64Service.b64Type(file);
                this.B64Service.fileToBase64(file).subscribe((fileString) => {
                    this.attachmentsToUpload.push(
                        {fileName: file.name, fileAsBase64: mime + fileString})
                })
            })
        }
    }

    backToTicketList() {
        this.router.navigate(['/tickets'])
    }

    isTicketOpen() {
        return this.selectedTicket.value.ticket.status === 'C'
    }

    retrieveAccessLogByCUID(cuid: string): Observable<any> {
        return this.http.get(`/api/secured/ticket/accesslog/retrieve/${cuid}`)
    }

}

export class TicketUser{
    allowGlobalTaskMaintenance: boolean;
    allowTickets: boolean;
    autoReassignTicketsToSupportRep: boolean;
    backEndProgrammer: boolean;
    extension: string;
    frontEndProgrammer: boolean;
    fullName: string;
    manager: string;
    phoneNumber: string;
    responsibleDepartment: string;
    supportRepresentative: boolean;
    tester: boolean;
    username: string;
}
